import React, {useState} from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    IconButton,
    Divider,
    Box,
    Stack,
    Tooltip,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {LotItem, LotWithItemList} from "../../../../../../types/lot";
import {useTranslation} from "react-i18next";

interface LotCardProps {
    lot: LotWithItemList;
    onAddItem?: (lotId: number) => void;
    onDeleteItem?: (lotId: number, itemId: number) => void;
    onDeleteLot?: (id: number, lotId?: number) => void;
    className?: string;
    maxItems?: number;
}

const LotCard: React.FC<LotCardProps> = ({
                                             lot,
                                             onAddItem,
                                             onDeleteItem,
                                             onDeleteLot,
                                             className,
                                             maxItems = 5
                                         }) => {
    const [expanded, setExpanded] = useState(false);

    // Отображаемые элементы в зависимости от режима expanded
    const displayedItems: LotItem[] = expanded ? lot.lotItems : lot.lotItems.slice(0, maxItems);
    const hasMoreItems = lot.lotItems.length > maxItems;

    // Обработчик удаления лота
    const handleDeleteLot = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onDeleteLot) {
            onDeleteLot(lot.id!, lot.lotId);
        }
    };

    const {t} = useTranslation();

    return (
        <Card
            className={className}
            sx={{
                width: '100%',
                boxShadow: 2,
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                }
            }}
        >
            <CardHeader
                title={
                    <Typography variant="h6" noWrap sx={{fontWeight: 'bold'}}>
                        {lot.companyName}
                    </Typography>
                }
                subheader={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 0.5}}>
                        <Chip
                            label={t(`lot_state.${lot.state}`)}
                            size="small"
                            color="primary"
                            sx={{height: 24}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {t('bin')}: {lot.itn}
                        </Typography>
                    </Stack>
                }
                action={
                    <Tooltip title={t('delete_lot')}>
                        <IconButton
                            aria-label={t('delete_lot')}
                            onClick={handleDeleteLot}
                            size="small"
                            color="error"
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                }
            />

            <Divider/>

            <CardContent sx={{pt: 2, pb: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1.5}}>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t('elements')} ({lot.lotItems.length})
                    </Typography>
                    <Typography variant="subtitle2">
                        {new Date(lot.auctionDate).toLocaleDateString()}
                    </Typography>
                </Box>

                {displayedItems.length > 0 ? (
                    <Stack spacing={1.5}>
                        {displayedItems.map((item: LotItem) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'background.default',
                                    '&:hover': {bgcolor: 'action.hover'}
                                }}
                            >
                                <Box sx={{overflow: 'hidden', flex: 1, pr: 1}}>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>
                                        {item.productName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                                        {item.productCode} - {item.productBrief}
                                    </Typography>
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 0.5}}>
                                        <Typography variant="caption">
                                            {item.amount} {item.unitName}
                                        </Typography>
                                        <Typography variant="caption" sx={{fontWeight: 500}}>
                                            {item.gross.toLocaleString()} {t('currency_tenge')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{py: 2}}>
                        {t('no_elements')}
                    </Typography>
                )}

                {hasMoreItems && !expanded && (
                    <Box sx={{mt: 1.5, textAlign: 'center'}}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(true)}
                        >
                            {t('show_all')} ({lot.lotItems.length})
                        </Button>
                    </Box>
                )}

                {expanded && hasMoreItems && (
                    <Box sx={{mt: 1.5, textAlign: 'center'}}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(false)}
                        >
                            {t('collapse')}
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default LotCard;
